import React, { useMemo, useRef, useState } from 'react';
import Button from '../../../components/common/Button/Button';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../../components/common/DataTable/DataTable';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import Icon from '../../../components/common/Icon/Icon';
import Loading from '../../../components/common/Loading/Loading';
import Pagination from '../../../components/common/Pagination/Pagination';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import SearchableSelect from '../../../components/common/SearchableSelect/SearchableSelect';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import SingleSelectFilter from '../../../components/common/SingleSelectFilter/SingleSelectFilter';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { StudentWorkoutHistoryImportService } from '../../../services/StudentWorkoutHistoryImportService';
import type { PaginationInfo } from '../../../types';
import { formatDate } from '../../../utils/formatting';
import {
  applyStudentHistoryMatches,
  chooseStudentHistoryMatch,
  formatHistoryMonth,
  historyMonthDate,
  parseStudentWorkoutHistoryImport,
  type StudentHistoryImportRow,
  type StudentHistoryImportStatus,
  type StudentHistoryMatchResult,
} from '../../../utils/studentWorkoutHistoryImport';
import styles from './ImportacoesTab.module.css';

const PAGE_SIZE = 20;
const STATUS_FILTERS = ['all', 'ready', 'ambiguous', 'duplicate', 'blocked', 'missing'] as const;
type HistoryStatusFilter = (typeof STATUS_FILTERS)[number];

const FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'historyPage' }),
  status: queryFilter.enum<HistoryStatusFilter>('all', STATUS_FILTERS, { param: 'historyStatus' }),
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'missing', label: 'Não encontradas' },
  { value: 'ready', label: 'Prontos para importar' },
  { value: 'ambiguous', label: 'Várias alunas' },
  { value: 'duplicate', label: 'Já cadastrados' },
  { value: 'blocked', label: 'Fora da importação' },
];

const WORKOUT_GRID = '36px minmax(190px, 1.6fr) minmax(140px, 1.2fr) minmax(140px, 1fr)';
const WORKOUT_HEADERS = ['', 'Mês', 'Código', 'Situação'];

interface StudentHistoryGroup {
  studentName: string;
  rows: StudentHistoryImportRow[];
}
const EMPTY_MATCH: StudentHistoryMatchResult = {
  studentsByName: {},
  trainingsByCode: {},
  existingCompetences: [],
};

const readErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error && error.message.trim().length > 0 ? error.message : fallback
);

const situationLabel = (row: StudentHistoryImportRow): string => {
  if (row.status === 'duplicate') {
    return 'Já cadastrado';
  }
  if (row.status === 'ready') {
    return 'Novo';
  }
  return row.reason ?? 'Fora da importação';
};

const nameClassName = (status: StudentHistoryImportStatus): string | undefined => {
  if (status === 'duplicate') {
    return styles.duplicateName;
  }
  if (status === 'blocked' || status === 'ambiguous') {
    return styles.blockedName;
  }
  return undefined;
};

interface StudentWorkoutHistoryImportProps {
  onBack: () => void;
}

const StudentWorkoutHistoryImport: React.FC<StudentWorkoutHistoryImportProps> = ({ onBack }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<StudentHistoryImportRow[]>([]);
  const [matchResult, setMatchResult] = useState<StudentHistoryMatchResult>(EMPTY_MATCH);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fileLoaded, setFileLoaded] = useState(false);
  const [expandedNames, setExpandedNames] = useState<Record<string, boolean>>({});

  const { filters, setFilter, resetFilters } = useQueryFilters(FILTER_SCHEMA, { pageKey: 'page' });
  const selectedCount = rows.filter((row) => selectedIds[row.id] && row.status === 'ready').length;
  const readyCount = rows.filter((row) => row.status === 'ready').length;
  const ambiguousCount = rows.filter((row) => row.status === 'ambiguous').length;
  const duplicateCount = rows.filter((row) => row.status === 'duplicate').length;
  const blockedCount = rows.filter((row) => row.status === 'blocked').length;
  const filteredRows = filters.status === 'all'
    ? rows
    : filters.status === 'missing'
      ? rows.filter((row) => row.reason === 'Aluna não encontrada')
      : rows.filter((row) => row.status === filters.status);

  const groups = useMemo<StudentHistoryGroup[]>(() => {
    const byName = new Map<string, StudentHistoryImportRow[]>();
    filteredRows.forEach((row) => {
      const current = byName.get(row.studentName) ?? [];
      current.push(row);
      byName.set(row.studentName, current);
    });

    return Array.from(byName.entries())
      .map(([studentName, studentRows]) => ({
        studentName,
        rows: studentRows.slice().sort((left, right) => (
          left.periodYear - right.periodYear
          || left.periodMonth - right.periodMonth
          || left.code.localeCompare(right.code, 'pt-BR')
        )),
      }))
      .sort((left, right) => left.studentName.localeCompare(right.studentName, 'pt-BR'));
  }, [filteredRows]);

  const pagination = useMemo<PaginationInfo>(() => {
    const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
    const currentPage = Math.min(filters.page, totalPages);
    return {
      page: currentPage,
      pageSize: PAGE_SIZE,
      totalRecords: groups.length,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    };
  }, [filters.page, groups.length]);

  const visibleGroups = groups.slice((pagination.page - 1) * PAGE_SIZE, pagination.page * PAGE_SIZE);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFile = async (file: File) => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    setRows([]);
    setSelectedIds({});
    setExpandedNames({});
    setMatchResult(EMPTY_MATCH);
    setFileLoaded(false);
    setFilter('page', 1);

    try {
      const text = await file.text();
      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error('Envie um JSON de histórico de treinos (workout-history).');
      }

      const parsed = parseStudentWorkoutHistoryImport(payload);
      const matched = await StudentWorkoutHistoryImportService.match(parsed);
      const applied = applyStudentHistoryMatches(parsed, matched);
      setMatchResult(matched);
      setRows(applied);
      setSelectedIds(Object.fromEntries(applied.map((row) => [row.id, row.status === 'ready'])));
      setFileLoaded(true);
    } catch (caught) {
      setError(readErrorMessage(caught, 'Não foi possível ler o arquivo.'));
    } finally {
      setLoading(false);
      resetFileInput();
    }
  };

  const toggleRow = (row: StudentHistoryImportRow) => {
    if (row.status !== 'ready') {
      return;
    }
    setSelectedIds((current) => ({ ...current, [row.id]: !current[row.id] }));
  };

  const toggleExpanded = (studentName: string) => {
    setExpandedNames((current) => ({ ...current, [studentName]: !current[studentName] }));
  };

  const visibleExpanded = visibleGroups.length > 0 && visibleGroups.every((group) => expandedNames[group.studentName]);

  const toggleVisibleExpanded = () => {
    setExpandedNames((current) => {
      const next = { ...current };
      visibleGroups.forEach((group) => {
        next[group.studentName] = !visibleExpanded;
      });
      return next;
    });
  };

  const toggleGroup = (groupRows: readonly StudentHistoryImportRow[]) => {
    const readyRows = groupRows.filter((row) => row.status === 'ready');
    if (readyRows.length === 0) {
      return;
    }
    const allSelected = readyRows.every((row) => selectedIds[row.id]);
    setSelectedIds((current) => {
      const next = { ...current };
      readyRows.forEach((row) => {
        next[row.id] = !allSelected;
      });
      return next;
    });
  };

  const handleChooseStudent = (studentName: string, value: string) => {
    const studentId = value.length > 0 ? Number(value) : null;
    const nextRows = chooseStudentHistoryMatch(
      rows,
      studentName,
      studentId !== null && Number.isInteger(studentId) ? studentId : null,
      matchResult.existingCompetences,
    );
    setRows(nextRows);
    setSelectedIds((current) => {
      const next = { ...current };
      nextRows.forEach((row) => {
        if (row.studentName !== studentName) {
          return;
        }
        next[row.id] = row.status === 'ready';
      });
      return next;
    });
  };

  const handleImport = async () => {
    const selected = rows.filter((row) => selectedIds[row.id] && row.status === 'ready' && row.studentId !== null);
    if (selected.length === 0) {
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await StudentWorkoutHistoryImportService.commit(selected);
      const importedIds = new Set(selected.map((row) => row.id));
      setRows((current) => current.map((row) => (
        importedIds.has(row.id)
          ? { ...row, status: 'duplicate', reason: 'Já cadastrado' }
          : row
      )));
      setSelectedIds(Object.fromEntries(rows.map((row) => [row.id, false])));
      setSuccess(`${result.importedCount} histórico${result.importedCount === 1 ? '' : 's'} importado${result.importedCount === 1 ? '' : 's'}.`);
    } catch (caught) {
      setError(readErrorMessage(caught, 'Não foi possível importar o histórico.'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <SectionTitle as="h2" text="Importar histórico de treinos" />
        <div className={styles.actions}>
          <Button type="button" variant="outline" size="medium" onClick={onBack} disabled={loading || importing}>
            Voltar
          </Button>
          <Button type="button" variant="outline" size="medium" onClick={() => fileInputRef.current?.click()} disabled={loading || importing}>
            Escolher JSON
          </Button>
          <Button type="button" size="medium" onClick={handleImport} disabled={loading || importing || selectedCount === 0}>
            {importing ? 'Importando...' : `Importar selecionados (${selectedCount})`}
          </Button>
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            accept="application/json,.json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleFile(file);
              }
            }}
          />
        </div>
      </div>

      {error && <p className={`${styles.message} ${styles.error}`}>{error}</p>}
      {success && <p className={`${styles.message} ${styles.success}`}>{success}</p>}
      {loading && <Loading />}

      {!loading && rows.length === 0 && !error && (
        <EmptyState
          message={fileLoaded ? 'Nenhum mês com código para importar.' : 'Escolha um JSON workout-history para ver a prévia.'}
          icon="upload_file"
          showImage={false}
        />
      )}

      {!loading && rows.length > 0 && (
        <>
          <p className={styles.summary}>
            {`${readyCount} pronto${readyCount === 1 ? '' : 's'} para importar, ${ambiguousCount} com várias alunas, ${duplicateCount} já cadastrado${duplicateCount === 1 ? '' : 's'}, ${blockedCount} fora da importação.`}
          </p>
          <FilterBar gap="sm" pushFirstChild className={styles.filters}>
            <div className={styles.studentToolbar}>
              <RecordCount
                count={`${groups.length} ${groups.length === 1 ? 'aluna' : 'alunas'}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="small"
                icon={visibleExpanded ? 'expand_less' : 'expand_more'}
                onClick={toggleVisibleExpanded}
                disabled={importing}
              >
                {visibleExpanded ? 'Ocultar todas' : 'Expandir todas'}
              </Button>
            </div>
            <FilterGroup>
              <SingleSelectFilter
                value={filters.status}
                onChange={(value) => setFilter('status', value as HistoryStatusFilter)}
                options={STATUS_FILTER_OPTIONS}
                placeholder="Situação"
              />
            </FilterGroup>
            {filters.status !== 'all' && (
              <ClearFiltersButton onClick={() => resetFilters()} />
            )}
          </FilterBar>
          {groups.length === 0 ? (
            <EmptyState message="Nenhuma aluna nesta situação." icon="filter_list" showImage={false} />
          ) : (
            <>
              <div className={styles.studentList}>
                {visibleGroups.map((group) => {
                  const readyRows = group.rows.filter((row) => row.status === 'ready');
                  const selectedReadyCount = readyRows.filter((row) => selectedIds[row.id]).length;
                  const candidates = group.rows.find((row) => row.candidateStudents.length > 0)?.candidateStudents ?? [];
                  const chosen = group.rows.find((row) => row.studentId !== null);
                  const matchedName = chosen?.matchedStudentName
                    ?? group.rows.find((row) => row.matchedStudentName)?.matchedStudentName
                    ?? null;
                  const uniformStatus = group.rows.every((row) => row.status === group.rows[0]?.status)
                    ? group.rows[0]?.status
                    : undefined;

                  const expanded = Boolean(expandedNames[group.studentName]);

                  return (
                    <section key={group.studentName} className={styles.studentGroup}>
                      <div className={styles.studentHeader}>
                        <input
                          type="checkbox"
                          checked={readyRows.length > 0 && selectedReadyCount === readyRows.length}
                          ref={(element) => {
                            if (element) {
                              element.indeterminate = selectedReadyCount > 0 && selectedReadyCount < readyRows.length;
                            }
                          }}
                          disabled={readyRows.length === 0 || importing}
                          aria-label={`Selecionar treinos de ${group.studentName}`}
                          onChange={() => toggleGroup(group.rows)}
                        />
                        <button
                          type="button"
                          className={styles.studentToggle}
                          aria-expanded={expanded}
                          onClick={() => toggleExpanded(group.studentName)}
                        >
                          <span className={uniformStatus ? nameClassName(uniformStatus) : undefined}>{group.studentName}</span>
                          <span className={styles.studentMeta}>
                            {`${group.rows.length} ${group.rows.length === 1 ? 'treino' : 'treinos'}`}
                          </span>
                          <Icon name={expanded ? 'expand_less' : 'expand_more'} size={20} className={styles.studentToggleIcon} />
                        </button>
                        {candidates.length > 0 ? (
                          <SearchableSelect
                            value={chosen?.studentId == null ? '' : String(chosen.studentId)}
                            onChange={(value) => handleChooseStudent(group.studentName, value)}
                            options={candidates.map((candidate) => ({
                              value: String(candidate.id),
                              label: candidate.fullName,
                            }))}
                            placeholder="Escolha a aluna"
                            searchPlaceholder="Buscar aluna"
                            disabled={importing}
                            allowEmpty
                            className={styles.studentSelect}
                          />
                        ) : (
                          <span className={styles.studentMeta}>
                            {matchedName ? `Aluna no banco: ${matchedName}` : 'Aluna no banco: —'}
                          </span>
                        )}
                      </div>
                      {expanded && (
                      <DataTable headers={WORKOUT_HEADERS} gridTemplate={WORKOUT_GRID} lastColumnIsActions={false} borderedRows>
                        {group.rows.map((row) => (
                          <DataTable.Row key={row.id}>
                            <input
                              type="checkbox"
                              checked={Boolean(selectedIds[row.id]) && row.status === 'ready'}
                              disabled={row.status !== 'ready' || importing}
                              aria-label={`Selecionar ${formatHistoryMonth(row.periodYear, row.periodMonth)}`}
                              onChange={() => toggleRow(row)}
                            />
                            <span>
                              {formatHistoryMonth(row.periodYear, row.periodMonth)}
                              <span className={styles.monthDates}>
                                {`Treino ${formatDate(historyMonthDate(row.periodYear, row.periodMonth, row.workoutDay))} · Entrega ${formatDate(historyMonthDate(row.periodYear, row.periodMonth, row.expectedDeliveryDay))}`}
                              </span>
                            </span>
                            <span>{row.code.length > 0 ? row.code : '—'}</span>
                            <span className={nameClassName(row.status)}>{situationLabel(row)}</span>
                          </DataTable.Row>
                        ))}
                      </DataTable>
                      )}
                    </section>
                  );
                })}
              </div>
              {pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                  <Pagination pagination={pagination} loading={importing} onPageChange={(nextPage) => setFilter('page', nextPage)} />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default StudentWorkoutHistoryImport;
